/**
 * form_submissions.answers — v2 question snapshot (client read/adapters).
 */

import { stripHtmlToText } from './richText';

export const SUBMISSION_ANSWERS_VERSION = 2;

const RESERVED_TOP_LEVEL_KEYS = new Set([
  'version',
  'score',
  'maxScore',
  'questions',
  '_scoring',
  '_meta',
  'points'
]);

export function isQuestionSnapshotAnswers(answers) {
  return Array.isArray(answers?.questions);
}

function snapshotQuestionFields(question) {
  const snap = {
    question_id: question.question_id,
    type: question.type,
    label: question.label ?? null,
    required: question.required === true,
    section: question.section || question.section_name || null
  };
  if (question.points != null) snap.points = question.points;
  if (question.scoring) snap.scoring = question.scoring;
  if (question.options) snap.options = question.options;
  if (question.allowMultiple != null) snap.allowMultiple = question.allowMultiple;
  if (question.awardAttemptPoints != null) snap.awardAttemptPoints = question.awardAttemptPoints;
  if (question.attemptPoints != null) snap.attemptPoints = question.attemptPoints;
  return snap;
}

export function rowToBreakdownEntry(row) {
  return {
    questionId: row.question_id,
    label: stripHtmlToText(row.label) || row.label || 'Untitled',
    earned: row.earned,
    max: row.max,
    correct: row.correct,
    manual: row.manual === true,
    pendingReview: row.pendingReview === true,
    manualOverride: row.manualOverride === true,
    graded_by: row.graded_by ?? null
  };
}

export function getSubmissionQuestions(answers) {
  if (!answers || typeof answers !== 'object') return [];
  if (isQuestionSnapshotAnswers(answers)) return answers.questions;
  return [];
}

export function getScoringBreakdown(answers) {
  const questions = getSubmissionQuestions(answers);
  if (questions.length) return questions.map(rowToBreakdownEntry);
  return answers?._scoring?.breakdown || [];
}

export function extractUserAnswers(answers) {
  if (!answers || typeof answers !== 'object') return {};
  if (isQuestionSnapshotAnswers(answers)) {
    const out = {};
    for (const row of answers.questions) {
      if (row?.question_id) out[row.question_id] = row.response ?? null;
    }
    return out;
  }
  const out = { ...answers };
  for (const key of RESERVED_TOP_LEVEL_KEYS) {
    delete out[key];
  }
  delete out.score;
  delete out.maxScore;
  return out;
}

/** Flat map for form canvas / FormRenderer. */
export function submissionAnswersToFormData(stored) {
  if (!stored || typeof stored !== 'object') return {};
  if (!isQuestionSnapshotAnswers(stored)) {
    const flat = { ...stored };
    delete flat.points;
    return flat;
  }

  const flat = {};
  for (const row of stored.questions) {
    if (row?.question_id) flat[row.question_id] = row.response ?? null;
  }
  if (stored.score != null) flat.score = stored.score;
  if (stored.maxScore != null) flat.maxScore = stored.maxScore;
  flat._scoring = {
    score: stored.score,
    maxScore: stored.maxScore,
    breakdown: stored.questions.map(rowToBreakdownEntry)
  };
  return flat;
}

export function getSubmissionScoreSummary(answers) {
  if (!answers || typeof answers !== 'object') return { score: null, maxScore: null };
  return {
    score: answers.score ?? answers.points ?? null,
    maxScore: answers.maxScore ?? null
  };
}

/** Merge flat UI edits back into a v2 snapshot for optimistic local state. */
export function mergeFormDataIntoSubmissionAnswers(stored, flatFormData, form) {
  if (!isQuestionSnapshotAnswers(stored)) return flatFormData;
  const responses = extractUserAnswers(flatFormData);
  const questions = (form?.questions || []).map((question) => {
    const qid = question.question_id;
    const prev = stored.questions.find((row) => row.question_id === qid) || {};
    const response = responses[qid] !== undefined ? responses[qid] : (prev.response ?? null);
    return {
      ...(prev.question_id ? prev : { ...snapshotQuestionFields(question) }),
      response,
      answered_by: prev.answered_by ?? null,
      answered_at: prev.answered_at ?? null,
      graded_by: prev.graded_by ?? null,
      graded_at: prev.graded_at ?? null,
      earned: prev.earned,
      max: prev.max,
      correct: prev.correct,
      manual: prev.manual,
      pendingReview: prev.pendingReview,
      manualOverride: prev.manualOverride
    };
  });

  const next = {
    ...stored,
    questions,
    score: flatFormData.score ?? stored.score,
    maxScore: flatFormData.maxScore ?? stored.maxScore
  };

  const breakdown = flatFormData._scoring?.breakdown;
  if (Array.isArray(breakdown) && breakdown.length) {
    const byQ = Object.fromEntries(breakdown.map((row) => [row.questionId, row]));
    next.questions = questions.map((row) => {
      const scored = byQ[row.question_id];
      if (!scored) return row;
      return {
        ...row,
        earned: scored.earned,
        max: scored.max ?? row.max,
        correct: scored.correct,
        manual: scored.manual,
        pendingReview: scored.pendingReview,
        manualOverride: scored.manualOverride,
        graded_by: scored.graded_by ?? row.graded_by
      };
    });
    if (flatFormData.score != null) next.score = flatFormData.score;
    if (flatFormData.maxScore != null) next.maxScore = flatFormData.maxScore;
  }

  return next;
}
